import { Box, Text } from '@chakra-ui/react';
import { Button, Icon as YaxleIcon, type IconName } from '../../../ui';
import { SETTINGS_BORDER_COLOR, SETTINGS_TABLE_GUTTER } from './constants';

export interface SettingsEmptyStateAction {
    label: string;
    onClick: () => void;
    isDisabled?: boolean;
}

export type SettingsEmptyStateVariant = 'page' | 'inset';

export interface SettingsEmptyStateProps {
    /** Glyph above the headline — the same Yaxle name the sidebar uses. */
    icon: IconName;
    /** Names what is missing: "No vendors configured yet". */
    title: string;
    /** What the first entry buys the viewer. Omit for a viewer who cannot add. */
    description?: string;
    /** Primary action inside the card. Omit when the viewer cannot add entries. */
    action?: SettingsEmptyStateAction;
    /**
     * `page` carries the table gutter and fills the remaining settings column
     * so the outline runs to the bottom of the page — the same space a table
     * would occupy. `inset` stays content-sized for a card that already sits
     * inside a padded column, such as a detail page.
     */
    variant?: SettingsEmptyStateVariant;
}

/**
 * What a settings section shows before anything is configured: one outlined
 * card holding the section glyph, the headline, a line of guidance, and the
 * action that fills the section.
 *
 * Every section uses this so the first screen reads the same everywhere. A
 * section with a search or filter keeps its own in-table "nothing matches" row
 * — that state means the entries exist but are hidden, which is not this.
 */
const SettingsEmptyState = ({
    icon,
    title,
    description,
    action,
    variant = 'page',
}: SettingsEmptyStateProps) => {
    const body = (
        <>
            <YaxleIcon name={icon} size={8} color="gray.400" mb={4} />
            <Text color="gray.500" fontSize="lg">
                {title}
            </Text>
            {description && (
                <Text color="gray.400" fontSize="sm" mt={2}>
                    {description}
                </Text>
            )}
            {action && (
                <Box mt={4}>
                    <Button
                        variant="solid"
                        onClick={action.onClick}
                        isDisabled={action.isDisabled}
                    >
                        {action.label}
                    </Button>
                </Box>
            )}
        </>
    );

    const card = (
        <Box
            p={8}
            textAlign="center"
            borderWidth="1px"
            borderColor={SETTINGS_BORDER_COLOR}
            borderRadius="lg"
            {...(variant === 'page'
                ? {
                      flex: 1,
                      minH: 0,
                      h: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                  }
                : {})}
        >
            {body}
        </Box>
    );

    if (variant === 'inset') return card;

    return (
        <Box
            px={SETTINGS_TABLE_GUTTER}
            pb={SETTINGS_TABLE_GUTTER}
            flex={1}
            minH={0}
            h="100%"
            w="100%"
            display="flex"
            flexDirection="column"
        >
            {card}
        </Box>
    );
};

export default SettingsEmptyState;
